"use client";

import { motion } from "framer-motion";
import { Sparkles, TrendingDown, CheckCircle2 } from "lucide-react";

export function KaiDemo() {
  return (
    <section
      id="kai"
      className="relative overflow-hidden py-24 lg:py-32"
    >
      <div
        className="absolute left-1/4 top-1/3 h-[500px] w-[600px] rounded-full opacity-20 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
        }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent-soft px-3 py-1 text-xs text-accent">
            <Sparkles size={12} />
            New in Uniskai — generally available
          </div>
          <h2 className="text-4xl font-medium tracking-tight text-foreground sm:text-5xl">
            Meet <span className="text-gradient-accent">Kai</span> — our AI
            FinOps Architect.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted">
            The newest feature inside Uniskai. A conversational AI built on
            Claude that reasons over your cloud, explains trade-offs, and
            applies the fixes you approve — in under a minute.
          </p>
        </div>

        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-surface-elevated shadow-2xl glow-accent">
            <div className="flex items-center gap-2 border-b border-border bg-surface px-5 py-3.5">
              <div className="flex gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
                <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
                <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
              </div>
              <div className="ml-4 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Claude Desktop</span>
                <span className="text-border">·</span>
                <span>uniskai MCP server</span>
                <span className="relative ml-1 flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
                </span>
              </div>
            </div>

            <div className="space-y-6 p-8 font-mono text-sm">
              <div>
                <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                  You
                </div>
                <div className="rounded-xl border border-border bg-background px-4 py-3 text-foreground">
                  Why did my AWS bill jump 40% this week? Can you fix it?
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-accent to-highlight">
                    <Sparkles size={10} className="text-foreground" />
                  </span>
                  Kai
                </div>
                <div className="rounded-xl border border-accent/30 bg-accent-soft px-4 py-3 leading-relaxed text-foreground">
                  <p>
                    Analyzed last 7 days across{" "}
                    <span className="text-highlight">14,382 resources</span>.
                    Here&apos;s what happened:
                  </p>
                  <div className="mt-3 space-y-2 font-sans text-[13px]">
                    <div className="flex items-start gap-2">
                      <TrendingDown
                        size={14}
                        className="mt-0.5 flex-shrink-0 text-success"
                      />
                      <span>
                        EKS <code className="text-highlight">prod-us</code>{" "}
                        scaled to 42 nodes after deploy{" "}
                        <code className="text-accent">rls-kai-42</code>.{" "}
                        <span className="text-muted-foreground">
                          14 never scaled down — stranded capacity.
                        </span>
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <TrendingDown
                        size={14}
                        className="mt-0.5 flex-shrink-0 text-success"
                      />
                      <span>
                        3 RDS snapshots from Q4 2025 untagged —{" "}
                        <span className="text-muted-foreground">
                          $840/mo orphaned.
                        </span>
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <TrendingDown
                        size={14}
                        className="mt-0.5 flex-shrink-0 text-success"
                      />
                      <span>
                        S3 versioning on{" "}
                        <code className="text-highlight">logs-archive</code>{" "}
                        accumulating —{" "}
                        <span className="text-muted-foreground">
                          lifecycle policy missing.
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-accent/20 pt-3 font-sans text-[13px]">
                    <div>
                      <span className="text-muted-foreground">Total waste:</span>{" "}
                      <span className="text-lg font-medium text-success">
                        $4,320/mo
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Confidence:</span>{" "}
                      <span className="text-foreground">97%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Risk:</span>{" "}
                      <span className="text-success">Low</span>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 font-sans text-xs">
                    <button className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-1.5 font-medium text-background transition-all hover:bg-muted">
                      <CheckCircle2 size={12} />
                      Apply all fixes
                    </button>
                    <button className="rounded-lg border border-border bg-surface px-3 py-1.5 font-medium text-muted transition-colors hover:text-foreground">
                      Review each
                    </button>
                    <button className="rounded-lg border border-border bg-surface px-3 py-1.5 font-medium text-muted transition-colors hover:text-foreground">
                      Generate report
                    </button>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.9 }}
              >
                <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                  You
                </div>
                <div className="rounded-xl border border-border bg-background px-4 py-3 text-foreground">
                  Apply all fixes
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 1.3 }}
              >
                <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-accent to-highlight">
                    <Sparkles size={10} className="text-foreground" />
                  </span>
                  Kai
                </div>
                <div className="rounded-xl border border-success/30 bg-success-soft px-4 py-3 font-sans leading-relaxed text-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-success" />
                    <span className="font-medium">Done.</span>
                  </div>
                  <div className="mt-2 space-y-1 text-[13px] text-muted">
                    <div>✓ Scaled down 14 EKS nodes in prod-us</div>
                    <div>✓ Removed 3 orphaned RDS snapshots</div>
                    <div>✓ Applied lifecycle policy to logs-archive</div>
                  </div>
                  <div className="mt-3 border-t border-success/20 pt-2 text-[13px]">
                    <span className="text-muted-foreground">
                      Saving starts today:
                    </span>{" "}
                    <span className="font-medium text-success">
                      $4,320/mo · $51,840/yr
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Demo reproduces a real Kai session. Actual time: 47 seconds.
          </p>
        </div>
      </div>
    </section>
  );
}
